/**
 * `<AgentActivityPill>` — top-bar pill showing the live agent state,
 * sliced from the conflated `Running` pill (#791, sliced from #782
 * defect 5).
 *
 * Three modes derived purely from `useActivitySnapshot()` +
 * `useInterviewStatus()`:
 *
 *   - `Ready` — session up, no agent in flight. Snapshot is `null`
 *     (no build registered for this game) OR every agent row has a
 *     terminal status AND `outstandingWaits` is empty AND no
 *     in-progress interview is pending.
 *   - `Running` — at least one row in `snapshot.byAgentId` carries
 *     `status: 'running'`. The visual breathes (CSS animation) so a
 *     user under uncertainty can see "yes, it's alive".
 *   - `Waiting on you` — `snapshot.outstandingWaits` is non-empty OR
 *     a build-game interview is in `'in-progress'`. Pill carries a
 *     wait-kind label when the snapshot tells us which gate is open
 *     (e.g. `Waiting on you — Plan review`, `... — Dependency
 *     install`, `... — Bulk interview`). Falls back to a bare
 *     `Waiting on you` when only the interview-status surface is
 *     standing.
 *
 * The conflation that motivated #791: the bifrost-phase pill carries
 * the literal copy `Running` whenever the deployed prototype on the
 * TV is alive. Users read it as "the agent is running" and stare at
 * the workspace expecting motion. This pill answers the agent-state
 * question directly so the bifrost pill can keep its prototype-state
 * meaning without confusion.
 *
 * Reduced-motion: the `Running` mode's breathe animation is wrapped
 * in `@media (prefers-reduced-motion: no-preference)`. The `Waiting`
 * mode carries an equivalent amber breathe (#829, ref #827 heartbeat
 * directive, cycle tightened to 1.8s by #870). For users with
 * `prefers-reduced-motion: reduce` the CSS animation is suppressed,
 * but a `<WaitingPillTextTick>` React component appends a cycling dot
 * count to the label text ("Waiting on you" → "Waiting on you." →
 * "…" → "…..") at 1s intervals — satisfying the ≤2s heartbeat rule
 * (#827) without any CSS animation. Ready is static for everyone.
 */

import { useState, useEffect } from 'react';
import type {
  ActivitySnapshot,
  WaitDescriptor,
} from '@volley/crucible-ipc-types/ipc-channels';
import type { BuildGameInterviewStatusSummary } from '@volley/crucible-ipc-types/build-game-types';
import type { WorkspaceRunState, WorkspaceRunTone } from '../workspace/run-state';
import { pickOldestWait } from './ActivityBanner';
import { formatWaitLabel } from '../wait-label';

export type AgentActivityMode = 'ready' | 'running' | 'waiting';

export interface AgentActivityModeDescriptor {
  mode: AgentActivityMode;
  /** Human label shown inside the pill (no prefix / em dash). */
  label: string;
  /**
   * Optional wait-kind suffix shown next to the label as
   * `Waiting on you — <waitLabel>`. Only set when `mode === 'waiting'`
   * AND the snapshot tells us which gate is open. Verbatim from the
   * wait descriptor's `reason` (which the gate author controls); when
   * the descriptor carries no usable reason, falls back to a
   * human-readable mapping of the `kind` field.
   */
  waitLabel: string | null;
}

const READY_LABEL = 'Ready';
const RUNNING_LABEL = 'Running';
const WAITING_LABEL = 'Waiting on you';

/**
 * Pure-derivation helper, exported for unit pinning. Reads BOTH
 * surfaces (activity snapshot AND interview-status summary) so the
 * waiting-on-you mode catches the pre-snapshot-registration window
 * — at the very start of `/build-game`, the SKILL writes
 * `status: 'in-progress'` to the interview state file before any
 * agent push lands. Without the interview-side check the pill would
 * spend the first few seconds reading `Ready` while a question is
 * already on screen.
 *
 * `snapshot === null` AND `interviewStatus !== 'in-progress'` is the
 * canonical `Ready` shape. Anything else is one of the active modes.
 */
export function deriveAgentActivityMode(args: {
  snapshot: ActivitySnapshot | null;
  interviewStatus: BuildGameInterviewStatusSummary['status'] | null | undefined;
}): AgentActivityModeDescriptor {
  const { snapshot, interviewStatus } = args;

  // Waiting takes priority over Running — when an outstanding wait
  // is open the agent has stopped emitting chunks (the gate is the
  // suspension point). Showing `Running` in that window is the bug
  // the issue calls out.
  if (snapshot !== null) {
    const wait = pickOldestWait(snapshot.outstandingWaits);
    if (wait !== null) {
      return { mode: 'waiting', label: WAITING_LABEL, waitLabel: formatWaitLabel(wait) };
    }
  }

  // Pre-snapshot interview-in-progress window. The SKILL has written
  // the state file but no agent push has landed yet (or the
  // `ActivityRegistry`'s post-done linger has cleared the snapshot
  // while the interview file still reads `in-progress`).
  if (interviewStatus === 'in-progress' && snapshot === null) {
    return { mode: 'waiting', label: WAITING_LABEL, waitLabel: null };
  }

  if (snapshot !== null) {
    for (const row of Object.values(snapshot.byAgentId)) {
      if (row.status === 'running') {
        return { mode: 'running', label: RUNNING_LABEL, waitLabel: null };
      }
    }
  }

  return { mode: 'ready', label: READY_LABEL, waitLabel: null };
}

/**
 * Style map. `running` carries the `agent-activity-pill--running`
 * class so the breathe animation in `globals.css` can target it; the
 * reduced-motion fallback in the same stylesheet leaves it as the
 * static green colour. `waiting` carries `agent-activity-pill--waiting`
 * for the equivalent amber breathe (see #829 / #827 heartbeat directive,
 * cycle tightened to 1.8s by #870).
 */
const MODE_STYLE: Record<AgentActivityMode, string> = {
  ready: 'bg-white/10 text-white/60',
  running: 'bg-green-500/20 text-green-400 agent-activity-pill--running',
  waiting: 'bg-amber-500/20 text-amber-300 agent-activity-pill--waiting',
};

const TONE_STYLE: Record<WorkspaceRunTone, string> = {
  neutral: 'bg-white/10 text-white/60',
  active: 'bg-green-500/20 text-green-400 agent-activity-pill--running',
  waiting: 'bg-amber-500/20 text-amber-300 agent-activity-pill--waiting',
  success: 'bg-green-500/20 text-green-300',
  warning: 'bg-amber-500/20 text-amber-300',
  error: 'bg-red-500/20 text-red-300',
};

/**
 * Dot suffixes cycled by `<WaitingPillTextTick>`. Each step is one
 * second apart, giving four distinct states that loop: bare label,
 * one dot, two dots, three dots.
 */
const TICK_DOTS = ['', '.', '..', '...'] as const;

/**
 * Leaf component that appends a cycling dot count to `baseText` every
 * 1 second using `setInterval`.
 *
 * Design rationale (#870 / #827):
 * - Under `prefers-reduced-motion: reduce` the `agent-activity-pill-
 *   waiting-breathe` CSS animation is suppressed (no CSS motion).
 *   Without this component the pill would be fully static for the
 *   entire wait duration, violating the ≤2s heartbeat rule from #827.
 * - This component uses React state + setInterval (not a CSS animation)
 *   so `prefers-reduced-motion` cannot suppress it.
 * - Under normal (motion-on) media, this component runs alongside the
 *   CSS breathing animation — the text tick and the visual breathe
 *   overlap harmlessly.
 *
 * Accessibility:
 * - The dot-count span carries `aria-hidden="true"` so screen readers
 *   do not announce the cycling dots every second. The base label text
 *   (e.g. "Waiting on you") is left visible to the accessibility tree.
 * - The outer pill element (`data-testid="agent-activity-pill"`) does
 *   NOT carry `aria-live` to prevent screen readers from re-reading
 *   the entire pill text on every tick.
 *
 * StrictMode safety:
 * - `useEffect` cleanup returns `clearInterval(id)`, so the double-
 *   mount cycle in React 18 StrictMode mounts → unmounts → re-mounts.
 *   The first interval is cleared on unmount; the second mount starts
 *   a fresh one. The visible dot count may skip a beat during the
 *   StrictMode double-mount but that is a development-only artefact.
 */
export function WaitingPillTextTick({
  baseText,
}: {
  baseText: string;
}): React.JSX.Element {
  const [tickIndex, setTickIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setTickIndex((prev) => (prev + 1) % TICK_DOTS.length);
    }, 1_000);
    return () => {
      clearInterval(id);
    };
  }, []);

  return (
    <>
      {baseText}
      {/*
        Width reservation (#870 / #827):
        The dot count cycles '' → '.' → '..' → '...' — if rendered without a
        fixed width, the pill reflows each tick. The harness from PR #874
        returns frameDiff=0 when locator screenshots have different
        dimensions, so the only motion signal under reduced-motion would
        be silently dropped. `inline-block w-[3ch] text-left` reserves
        enough room for the longest state ('...') so width stays constant
        across all four tick states.
      */}
      <span
        aria-hidden="true"
        data-testid="waiting-pill-tick-dots"
        className="inline-block w-[3ch] text-left"
      >
        {TICK_DOTS[tickIndex]}
      </span>
    </>
  );
}

export function AgentActivityPill({
  snapshot,
  interviewStatus,
  runState,
}: {
  snapshot: ActivitySnapshot | null;
  interviewStatus: BuildGameInterviewStatusSummary['status'] | null | undefined;
  runState?: WorkspaceRunState | null;
}): React.JSX.Element {
  if (runState) {
    const isWaiting = runState.tone === 'waiting';
    const isActive = runState.tone === 'active';
    const mode: AgentActivityMode = isWaiting ? 'waiting' : isActive ? 'running' : 'ready';
    const baseText = runState.detail
      ? `${runState.label} — ${runState.detail}`
      : runState.label;
    return (
      <span
        data-testid="agent-activity-pill"
        data-mode={mode}
        data-run-kind={runState.kind}
        data-debug-source={runState.debugSource}
        className={`mr-2 inline-flex min-h-[22px] items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_STYLE[runState.tone]}`}
        title={runState.debugSource}
      >
        {isWaiting ? (
          <WaitingPillTextTick baseText={baseText} />
        ) : isActive ? (
          <>
            {baseText}
            <span
              aria-hidden="true"
              className="animated-ellipsis inline-block w-[3ch] text-left"
            />
          </>
        ) : (
          baseText
        )}
      </span>
    );
  }

  const descriptor = deriveAgentActivityMode({ snapshot, interviewStatus });
  const baseText =
    descriptor.waitLabel !== null
      ? `${descriptor.label} — ${descriptor.waitLabel}`
      : descriptor.label;
  return (
    <span
      data-testid="agent-activity-pill"
      data-mode={descriptor.mode}
      data-wait-label={descriptor.waitLabel ?? ''}
      className={`mr-2 rounded-full px-2.5 py-0.5 text-xs font-medium ${MODE_STYLE[descriptor.mode]}`}
    >
      {descriptor.mode === 'waiting' ? (
        <WaitingPillTextTick baseText={baseText} />
      ) : (
        baseText
      )}
    </span>
  );
}
